import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Progress } from '../ui/Progress';
import { 
  ArrowLeft, 
  Phone, 
  Video, 
  MessageSquare, 
  Download, 
  Star, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  User,
  Briefcase,
  GraduationCap,
  MapPin,
  Clock,
  TrendingUp,
  Target,
  Lightbulb,
  Users
} from 'lucide-react';
import { apiClient } from '../../lib/api/client';
import { toast } from 'react-hot-toast';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';

interface AnalysisResults {
  analysis_id: string;
  candidate_id: string;
  requirement_id: string;
  fitment_score: number;
  overall_assessment: string;
  strengths: string[];
  gaps: string[];
  interview_questions: string[];
  risks: string[];
  recommendations: string[];
  created_at: string;
  completed_at: string;
}

interface Candidate {
  id: string;
  name: string;
  email: string;
  phone?: string;
  parsed_fields?: {
    skills?: string[];
    experience_years?: number;
    education?: string;
    location?: string;
    summary?: string;
    work_experience?: Array<{
      company: string;
      position: string;
      duration: string;
      description: string;
    }>;
  };
}

interface Requirement {
  id: string;
  title: string;
  client_name?: string;
  location?: {
    city: string;
    state?: string;
    country: string;
  };
  skills?: {
    mandatory_skills: string[];
    optional_skills: string[];
  };
  experience?: {
    minYears: number;
    maxYears?: number;
  };
  description?: string;
}

const AnalysisResultsPage: React.FC = () => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [requirement, setRequirement] = useState<Requirement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (analysisId) {
      loadAnalysisData();
    }
  }, [analysisId]);

  const loadAnalysisData = async () => {
    try {
      setLoading(true);
      
      // Load analysis results
      const results = await apiClient.getAnalysisResults(analysisId!);
      setAnalysisResults(results);
      
      // Load candidate data
      const candidateData = await apiClient.getCandidate(results.candidate_id);
      setCandidate(candidateData);
      
      // Load requirement data
      const requirementData = await apiClient.getRequirement(results.requirement_id);
      setRequirement(requirementData);
      
    } catch (error: any) {
      console.error('Failed to load analysis data:', error);
      setError(error.response?.data?.detail || 'Failed to load analysis results');
      toast.error('Failed to load analysis results');
    } finally {
      setLoading(false);
    }
  };

  const getFitmentScoreColor = (score: number) => {
    const s = Math.max(0, Math.min(100, Math.round(score)));
    const bucket = Math.floor(s / 5);
    const palette = [
      'text-red-700 bg-red-100/60','text-red-700 bg-red-100/60','text-red-600 bg-red-100/50','text-red-500 bg-red-100/40',
      'text-orange-600 bg-orange-100/50','text-orange-500 bg-orange-100/50','text-amber-600 bg-amber-100/50','text-amber-500 bg-amber-100/50',
      'text-yellow-600 bg-yellow-100/50','text-yellow-500 bg-yellow-100/50','text-lime-600 bg-lime-100/50','text-lime-500 bg-lime-100/50',
      'text-green-600 bg-green-100/50','text-green-500 bg-green-100/50','text-green-500 bg-green-100/50','text-emerald-600 bg-emerald-100/50',
      'text-emerald-500 bg-emerald-100/50','text-teal-600 bg-teal-100/50','text-teal-500 bg-teal-100/50','text-teal-500 bg-teal-100/50','text-teal-500 bg-teal-100/50'
    ];
    return palette[Math.min(palette.length - 1, bucket)];
  };

  const getFitmentScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (score >= 60) return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    return <XCircle className="w-5 h-5 text-red-600" />;
  };

  const handleCall = (type: 'phone' | 'video' | 'message') => {
    if (!candidate?.phone) {
      toast.error('Candidate phone number not available');
      return;
    }
    
    switch (type) {
      case 'phone':
        window.open(`tel:${candidate.phone}`);
        break;
      case 'video':
        // In a real app, this would integrate with video calling service
        toast.success('Video call feature coming soon!');
        break;
      case 'message':
        // In a real app, this would open messaging interface
        toast.success('Messaging feature coming soon!');
        break;
    }
  };

  const handleDownloadReport = async () => {
    try {
      // In a real app, this would generate and download a PDF report
      toast.success('Report download feature coming soon!');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return (
      <ResponsiveContainer>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-foreground-600">Loading analysis results...</p>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (error || !analysisResults) {
    return (
      <ResponsiveContainer>
        <div className="text-center py-12">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">Analysis Not Found</h2>
          <p className="text-foreground-600 mb-6">{error || 'The analysis results could not be loaded.'}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground-900">Analysis Results</h1>
              <p className="text-foreground-600">
                JD vs CV Analysis for {candidate?.name} - {requirement?.title}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleDownloadReport}
              variant="outline"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Report
            </Button>
          </div>
        </div>

        {/* Fitment Score Overview */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground-900">Fitment Analysis</h2>
            <div className="flex items-center gap-2">
              {getFitmentScoreIcon(analysisResults.fitment_score)}
              <span className="text-sm text-foreground-600">Completed</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Overall Score */}
            <div className="text-center">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold ${getFitmentScoreColor(analysisResults.fitment_score)}`}>
                {analysisResults.fitment_score}
              </div>
              <p className="text-sm text-foreground-600 mt-2">Overall Fitment Score</p>
            </div>
            
            {/* Progress Bar */}
            <div className="md:col-span-2">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-600">Match Quality</span>
                  <span className="font-medium">{analysisResults.fitment_score}%</span>
                </div>
                <Progress value={analysisResults.fitment_score} className="h-3" />
                <p className="text-sm text-foreground-600">
                  {analysisResults.fitment_score >= 80 ? 'Excellent Match' : 
                   analysisResults.fitment_score >= 60 ? 'Good Match' : 'Needs Review'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Overall Assessment */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-foreground-900 mb-2">Overall Assessment</h3>
            <p className="text-foreground-700">{analysisResults.overall_assessment}</p>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Candidate Information */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-foreground-900">Candidate Profile</h3>
            </div>
            
            {candidate && (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-foreground-900">{candidate.name}</h4>
                  <p className="text-sm text-foreground-600">{candidate.email}</p>
                  {candidate.phone && (
                    <p className="text-sm text-foreground-600">{candidate.phone}</p>
                  )}
                </div>
                
                {candidate.parsed_fields && (
                  <div className="space-y-3">
                    {candidate.parsed_fields.experience_years && (
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-foreground-500" />
                        <span className="text-sm">{candidate.parsed_fields.experience_years} years experience</span>
                      </div>
                    )}
                    
                    {candidate.parsed_fields.education && (
                      <div className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-foreground-500" />
                        <span className="text-sm">{candidate.parsed_fields.education}</span>
                      </div>
                    )}
                    
                    {candidate.parsed_fields.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-foreground-500" />
                        <span className="text-sm">{candidate.parsed_fields.location}</span>
                      </div>
                    )}
                    
                    {candidate.parsed_fields.skills && candidate.parsed_fields.skills.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-foreground-900 mb-2">Key Skills:</p>
                        <div className="flex flex-wrap gap-1">
                          {candidate.parsed_fields.skills.slice(0, 5).map((skill, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                          {candidate.parsed_fields.skills.length > 5 && (
                            <Badge variant="outline" className="text-xs">
                              +{candidate.parsed_fields.skills.length - 5} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Job Requirement */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Briefcase className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-foreground-900">Job Requirement</h3>
            </div>
            
            {requirement && (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-foreground-900">{requirement.title}</h4>
                  {requirement.client_name && (
                    <p className="text-sm text-foreground-600">Client: {requirement.client_name}</p>
                  )}
                  {requirement.location && (
                    <p className="text-sm text-foreground-600">
                      {requirement.location.city}, {requirement.location.country}
                    </p>
                  )}
                </div>
                
                {requirement.experience && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-foreground-500" />
                    <span className="text-sm">
                      {requirement.experience.minYears}
                      {requirement.experience.maxYears ? `-${requirement.experience.maxYears}` : '+'} years
                    </span>
                  </div>
                )}
                
                {requirement.skills && (
                  <div>
                    <p className="text-sm font-medium text-foreground-900 mb-2">Required Skills:</p>
                    <div className="flex flex-wrap gap-1">
                      {requirement.skills.mandatory_skills.slice(0, 5).map((skill, index) => (
                        <Badge key={index} variant="default" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                      {requirement.skills.mandatory_skills.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{requirement.skills.mandatory_skills.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Analysis Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Strengths */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-foreground-900">Strengths</h3>
            </div>
            
            <div className="space-y-3">
              {analysisResults.strengths.map((strength, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <Star className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground-700">{strength}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Gaps */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <h3 className="text-lg font-semibold text-foreground-900">Areas for Improvement</h3>
            </div>
            
            <div className="space-y-3">
              {analysisResults.gaps.map((gap, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                  <Target className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground-700">{gap}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Interview Questions */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-foreground-900">Recommended Interview Questions</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analysisResults.interview_questions.map((question, index) => (
              <div key={index} className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-sm text-foreground-700">{question}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Risks and Recommendations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Risks */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-foreground-900">Potential Risks</h3>
            </div>
            
            <div className="space-y-3">
              {analysisResults.risks.map((risk, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                  <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground-700">{risk}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Recommendations */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Lightbulb className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-foreground-900">Recommendations</h3>
            </div>
            
            <div className="space-y-3">
              {analysisResults.recommendations.map((recommendation, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground-700">{recommendation}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-foreground-900">Next Steps</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              onClick={() => handleCall('phone')}
              className="w-full"
              disabled={!candidate?.phone}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Candidate
            </Button>
            
            <Button
              onClick={() => handleCall('video')}
              variant="outline"
              className="w-full"
            >
              <Video className="w-4 h-4 mr-2" />
              Video Interview
            </Button>
            
            <Button
              onClick={() => handleCall('message')}
              variant="outline"
              className="w-full"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </div>
          
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-foreground-600">
              <strong>Analysis completed:</strong> {new Date(analysisResults.completed_at).toLocaleString()}
            </p>
          </div>
        </Card>
      </div>
    </ResponsiveContainer>
  );
};

export default AnalysisResultsPage;

